import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { getFilteredNavTree, isItemActive, FeedbackIcon, HelpIcon } from '@/lib/nav-tree';
import { NavItem } from './NavItem';
import { NavSection } from './NavSection';
import { useUnreadChatCount } from '@/hooks/use-unread-chat-count';

// Sprint 38 — Contractual Precision left rail.
//
// Renders the workflow IA. Filters by current user's permissions. Emits
// onOpenFeedback when the bottom Feedback button is clicked (the modal
// itself is provided by Phase 4.5 Feedback feature; until then it's a
// no-op placeholder).
//
// Responsive widths are controlled by the parent <AppShell> via the
// `collapsed` prop:
//   collapsed=true  → tablet/icon-only (40px wide effectively, centered icons)
//   collapsed=false → desktop (240px), full labels + counters
//
// Sprint 41 — Footer order (top-to-bottom) is Settings / Help / Feedback
// per the approved dashboard mockup. The Help link points at the public
// support site; it is labelled 'Help' to match every other place the
// product references it.

interface NavRailProps {
  collapsed?: boolean;
  onOpenFeedback?: () => void;
  onItemSelect?: () => void;     // closes mobile drawer on nav
  /** When provided, the rail renders a double-arrow toggle that lets the
   *  user collapse/expand the desktop rail. Tablet auto-collapse omits
   *  this since the rail is forced collapsed at that breakpoint. */
  onToggleCollapsed?: () => void;
  brand?: React.ReactNode;
  className?: string;
  /** Bumps when usePermissionsSync refreshes the stored access set so the
   *  filtered nav tree recomputes without a full remount. */
  accessVersion?: number;
}

export function NavRail({
  collapsed = false,
  onOpenFeedback,
  onItemSelect,
  onToggleCollapsed,
  brand,
  className,
  accessVersion = 0,
}: NavRailProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const tree = React.useMemo(() => getFilteredNavTree(), [accessVersion]);

  // H10 — Help module isn't released yet. Show a "coming soon" popup and send
  // the user back to their dashboard instead of opening the support site.
  const handleHelpClick = () => {
    toast({
      title: 'Help is coming soon',
      description: 'The Help module will be released in a future update.',
    });
    onItemSelect?.();
    navigate('/dashboard');
  };

  // #4 — resolve nav counter badges. Currently only the chat unread count.
  const unreadChatCount = useUnreadChatCount();
  const resolveCounter = (counterKey?: string): number | undefined =>
    counterKey === 'chat' ? unreadChatCount : undefined;

  return (
    <nav
      aria-label="Primary navigation"
      data-testid="nav-rail"
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden',
        'bg-sidebar text-sidebar-foreground border-r-2 border-border',
        className,
      )}
    >
      {brand && (
        <div className={cn('shrink-0 border-b-2 border-border', collapsed ? 'p-3' : 'p-4')}>
          {brand}
        </div>
      )}

      {/* Top collapse toggle — mirrors the bottom one so the user can
          collapse/expand the rail from either end and reclaim screen width. */}
      {onToggleCollapsed && (
        <div className="shrink-0 border-b-2 border-border py-2">
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            className={cn(
              'group relative flex w-full items-center gap-3 px-4 py-2 font-public-sans text-sm font-semibold uppercase tracking-[0.04em]',
              'text-sidebar-foreground hover:bg-foreground/[0.05] dark:hover:bg-foreground/[0.10] hover:text-sidebar-active dark:hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              collapsed && 'justify-center px-2',
            )}
          >
            {collapsed ? (
              <ChevronsRight className="h-4 w-4 shrink-0" aria-hidden="true" />
            ) : (
              <ChevronsLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
            )}
            {!collapsed && <span className="truncate">Collapse</span>}
          </button>
        </div>
      )}

      <div
        data-testid="nav-rail-items"
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain py-2"
      >
        {tree.primary.map((item) => (
          <NavItem
            key={item.url}
            label={item.label}
            url={item.url}
            icon={item.icon}
            active={isItemActive(item, pathname)}
            collapsed={collapsed}
            onClick={onItemSelect}
          />
        ))}
        {tree.sections.map((section) => (
          <NavSection key={section.label} label={section.label} collapsed={collapsed}>
            {section.items.map((item) => (
              <NavItem
                key={item.url}
                label={item.label}
                url={item.url}
                icon={item.icon}
                active={isItemActive(item, pathname)}
                counter={resolveCounter(item.counterKey)}
                collapsed={collapsed}
                onClick={onItemSelect}
              />
            ))}
          </NavSection>
        ))}
      </div>

      {/* Bottom-of-rail utility entries — Settings (from NavTree.utility),
          Help (external support link), Feedback (modal trigger), and the
          optional Collapse toggle. Order matches the approved dashboard mockup. */}
      <div
        data-testid="nav-rail-utility"
        className="shrink-0 border-t-2 border-border py-2"
      >
        {tree.utility.map((item) => (
          <NavItem
            key={item.url}
            label={item.label}
            url={item.url}
            icon={item.icon}
            active={isItemActive(item, pathname)}
            collapsed={collapsed}
            onClick={onItemSelect}
          />
        ))}
        <button
          type="button"
          onClick={handleHelpClick}
          aria-label="Open help"
          className={cn(
            'group relative flex w-full items-center gap-3 px-4 py-2 font-public-sans text-sm font-semibold uppercase tracking-[0.04em]',
            'text-sidebar-foreground hover:bg-foreground/[0.05] dark:hover:bg-foreground/[0.10] hover:text-sidebar-active dark:hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            collapsed && 'justify-center px-2',
          )}
        >
          <HelpIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {!collapsed && <span className="truncate">Help</span>}
        </button>
        <button
          type="button"
          onClick={onOpenFeedback}
          aria-label="Open feedback"
          className={cn(
            'group relative flex w-full items-center gap-3 px-4 py-2 font-public-sans text-sm font-semibold uppercase tracking-[0.04em]',
            'text-sidebar-foreground hover:bg-foreground/[0.05] dark:hover:bg-foreground/[0.10] hover:text-sidebar-active dark:hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            collapsed && 'justify-center px-2',
          )}
        >
          <FeedbackIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {!collapsed && <span className="truncate">Feedback</span>}
        </button>
        {onToggleCollapsed && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            className={cn(
              'group relative flex w-full items-center gap-3 px-4 py-2 font-public-sans text-sm font-semibold uppercase tracking-[0.04em]',
              'text-sidebar-foreground hover:bg-foreground/[0.05] dark:hover:bg-foreground/[0.10] hover:text-sidebar-active dark:hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              collapsed && 'justify-center px-2',
            )}
          >
            {collapsed ? (
              <ChevronsRight className="h-4 w-4 shrink-0" aria-hidden="true" />
            ) : (
              <ChevronsLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
            )}
            {!collapsed && <span className="truncate">Collapse</span>}
          </button>
        )}
      </div>
    </nav>
  );
}
